import _Button from "@mui/material/Button";
import _CircularProgress from "@mui/material/CircularProgress";
import _ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import _ToggleButton from "@mui/material/ToggleButton";
import _Divider from "@mui/material/Divider";
import _Tabs from "@mui/material/Tabs";
import _Tab from "@mui/material/Tab";
import _Box from "@mui/material/Box";
import _Typography from "@mui/material/Typography";
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import explorationMapFlag from '../../assets/images/bfg/exploration-map-flag.svg';
import explorationMapPreview from '../../assets/images/bfg/exploration-map-preview.png';
import { getCampaignFamilyConfig } from '../../event-campaigns';
import { useAmplitudeTracker } from '../../hooks/tracker';
import useGeogroupQuery from '../../hooks/use-geogroup-query';
import { ChallengeCardData } from '../challenge-card';
import { EventLeaderboard } from '../event/leaderboard';
import { ExportIcon, ParticipantsIcon } from '../icons';
import BfgFAQ from './bfg-faq';
import BttfFAQ from './bttf-faq';
import BttfPointsRules from './bttf-points-rules';
import CompaniesLeaderboard from './companies-leaderboard';
import CompaniesLeaderboardHome from './companies-leaderboard/home';
import CompanyLeaderboard, { fakeSites, fakeTeams, fakeRegions } from './company-leaderboard';
import CustomPointsDialogButton from './custom-points-dialog-button';
import EventStatusSubtitle from './event-status-subtitle';
import ExplorationMapGame from './exploration-map-game';
import ExplorationMapGameIntergroup from './exploration-map-game-intergroup';
import BfgHome from './home/bfg-home';
import BfgScoreDetailCard from './home/bfg-score-detail-card';
import BttfHome from './home/bttf-home';
import MavHome from './home/mav-home';
import IndividualLeaderboard from './individual-leaderboard';
import IndividualLeaderboardHome from './individual-leaderboard/home';
import LeaderboardScoreFutureCard from './leaderboard-score-future-card';
import SitesEventLeaderboard from './sites-event-leaderboard';
import { eventsDetailsTab, eventsDetailsTabMap, eventHasHomeTab, getEventFamily } from './tabs';
const leaderboardToggleSx = {
  bgcolor: 'rgba(221, 224, 231, 0.5)',
  borderRadius: 8,
  p: 0.5,
  width: 'fit-content',
  '& .MuiToggleButton-root': {
    border: 'none',
    borderRadius: '999px !important',
    color: '#666a6f',
    fontSize: 14,
    fontWeight: 600,
    paddingX: 2,
    paddingY: 1,
    textTransform: 'none',
    '&.Mui-selected': {
      bgcolor: '#fff',
      boxShadow: '0px 3px 4px rgba(0,0,0,0.12), 0px 3px 0.5px rgba(0,0,0,0.04)',
      color: 'text.primary',
      '&:hover': {
        bgcolor: '#fff'
      }
    }
  }
};
export function EventsDetails({
  disableIndividualLeaderboard,
  disableInternalLeaderboard,
  disableSitesLeaderboard,
  enrolledGeogroupParentId,
  internalLeaderboardDisplayTypes,
  leaderboardDisplayed,
  holdingGroupId,
  holdingGroupName,
  isMember,
  defaultEntityTypes,
  ...props
}) {
  const {
    Button,
    Dialog,
    event,
    ownGeogroup,
    selectedTab,
    selectTab,
    leaderboardType,
    selectLeaderboardType
  } = props;
  const [searchParams, setSearchParams] = useSearchParams();
  const [leaderboardScope, setLeaderboardScope] = useState(() => searchParams.get('scope') === 'internal' ? 'internal' : 'national');
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (selectedTab === 'leaderboard') {
      params.set('scope', leaderboardScope);
      params.set('ranking-type', leaderboardType);
    } else {
      params.delete('scope');
      params.delete('ranking-type');
    }
    setSearchParams(params, {
      replace: true
    });
  }, [selectedTab, leaderboardScope, leaderboardType]);
  const [focusOwnCompanyIndex, setFocusOwnCompanyIndex] = useState();
  const [focusOwnCompanyRequestId, setFocusOwnCompanyRequestId] = useState(0);
  const [leaderboardExportHandler, setLeaderboardExportHandler] = useState();
  const [exportingLeaderboard, setExportingLeaderboard] = useState(false);
  const {
    t
  } = useTranslation();
  const {
    trackEvent
  } = useAmplitudeTracker();
  const {
    data: fetchedDefaultParticipationGroup
  } = useGeogroupQuery(event.defaultParticipationGroup ?? undefined);
  const defaultParticipationGroup = event.defaultParticipationGroup ? fetchedDefaultParticipationGroup : null;
  const tabsDragRef = useRef({
    down: false,
    moved: false,
    startX: 0,
    scrollLeft: 0,
    scroller: null
  });
  const tabsRef = useRef(null);
  useEffect(() => {
    return () => {
      document.body.style.userSelect = '';
      document.removeEventListener('mousemove', handleTabsMouseMove);
      document.removeEventListener('mouseup', handleTabsMouseUp);
    };
  }, []);
  useEffect(() => {
    const scroller = tabsRef.current?.querySelector('.MuiTabs-scroller');
    if (!scroller) return;
    function handleWheel(wheelEvent) {
      if (Math.abs(wheelEvent.deltaY) <= Math.abs(wheelEvent.deltaX)) return;
      wheelEvent.preventDefault();
      scroller.scrollLeft += wheelEvent.deltaY;
    }
    scroller.addEventListener('wheel', handleWheel, {
      passive: false
    });
    return () => scroller.removeEventListener('wheel', handleWheel);
  }, []);
  function handleTabsMouseDown(mouseDownEvent) {
    const scroller = mouseDownEvent.currentTarget.querySelector('.MuiTabs-scroller');
    if (!scroller) return;
    tabsDragRef.current = {
      down: true,
      moved: false,
      startX: mouseDownEvent.clientX,
      scrollLeft: scroller.scrollLeft,
      scroller
    };
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', handleTabsMouseMove);
    document.addEventListener('mouseup', handleTabsMouseUp);
  }
  function handleTabsMouseMove(mouseMoveEvent) {
    const drag = tabsDragRef.current;
    if (!drag.down || !drag.scroller) return;
    const delta = mouseMoveEvent.clientX - drag.startX;
    if (Math.abs(delta) > 3) drag.moved = true;
    drag.scroller.scrollLeft = drag.scrollLeft - delta;
  }
  function handleTabsMouseUp() {
    tabsDragRef.current.down = false;
    document.body.style.userSelect = '';
    document.removeEventListener('mousemove', handleTabsMouseMove);
    document.removeEventListener('mouseup', handleTabsMouseUp);
  }
  const isBttf = event.code?.toLowerCase() === 'bttf';
  const isBfg = getEventFamily(event.code) === 'bfg';
  const familyConfig = getCampaignFamilyConfig(event.code);
  const hasLeaderboardScoreFutureCard = !!familyConfig?.hasLeaderboardScoreFutureCard;
  const hasHomeTab = isBttf || !!familyConfig?.hasHomeTab;
  const hasFaqTab = isBttf || !!familyConfig?.hasFaqTab;
  const hasExplorationMapTab = !props.isHolding && event.h3GameId !== null;
  const isProd = process.env.REACT_APP_ENV === 'production';
  const isGeoveloPartner = props.partner?.id === (isProd ? 1 : 5);
  const hasExplorationMapTestTab = isGeoveloPartner && hasExplorationMapTab && (event.period === 'current' || event.period === 'past');
  const hasStatsTab = !props.isHolding && !!familyConfig?.hasStatsTab && !!props.employee?.isAdminOrAnimator;
  const hasPotentialTab = !props.isHolding && !!familyConfig?.hasPotentialTab && !!props.employee?.isAdminOrAnimator;
  const hasLotsTab = !!familyConfig?.hasLotsTab;
  const hasInternalLeaderboardTabs = !disableInternalLeaderboard || !disableSitesLeaderboard;
  const leaderboardExportSupported = leaderboardScope === 'national' ? !(isBttf && leaderboardType === 'companies') : !disableInternalLeaderboard || !!enrolledGeogroupParentId;

  // Exporting every cyclist of the national leaderboard isn't allowed: keep the button visible
  // but greyed out.
  const isNationalCyclistsLeaderboard = leaderboardScope === 'national' && leaderboardType === 'individual';
  function handleOwnCompanyClick() {
    selectTab('leaderboard');
    setLeaderboardScope('internal');
  }
  function handleFocusOwnCompanyInLeaderboard(leaderboardIndex) {
    selectTab('leaderboard');
    setLeaderboardScope('national');
    setFocusOwnCompanyIndex(leaderboardIndex);
    setFocusOwnCompanyRequestId(id => id + 1);
  }

  // Memoized so the reference stays stable across renders — otherwise it's a new function every
  // time, which retriggers the effects in the leaderboard components that call it on every render
  // of this component (each call replaces `leaderboardExportHandler` with a new function value,
  // which forces a re-render here, producing a new `handleLeaderboardExportReady` reference,
  // looping forever with "Maximum update depth exceeded").
  const handleLeaderboardExportReady = useCallback(handler => {
    // Functions can't be set directly as state (React treats a function argument to a state
    // setter as a functional updater) — wrap it so the handler itself becomes the new state.
    setLeaderboardExportHandler(() => handler);
  }, [setLeaderboardExportHandler]);
  async function handleExportLeaderboardClick() {
    if (!leaderboardExportHandler) return;
    trackEvent('Event Leaderboard Export Clicked', {
      event_id: event.id,
      scope: leaderboardScope
    });
    setExportingLeaderboard(true);
    try {
      await leaderboardExportHandler();
    } finally {
      setExportingLeaderboard(false);
    }
  }
  if (leaderboardDisplayed) {
    if (leaderboardType === 'individual') {
      return /*#__PURE__*/React.createElement(IndividualLeaderboard, _extends({}, props, {
        isMember: isMember
      }));
    }
    return isBttf ? /*#__PURE__*/React.createElement(CompaniesLeaderboard, props) : /*#__PURE__*/React.createElement(EventLeaderboard, {
      hideTabs: true,
      hideTitle: true,
      currentUser: props.currentUser,
      defaultEntityTypes: defaultEntityTypes,
      event: event,
      holdingGroupId: holdingGroupId,
      holdingGroupName: holdingGroupName,
      isMember: isMember,
      ownGeogroup: ownGeogroup
    });
  }
  const challengeCardBlock = /*#__PURE__*/React.createElement(_Box, {
    bgcolor: "#fff",
    border: "1px solid #D9E7FF",
    borderRadius: 4,
    marginBottom: 2,
    padding: 3
  }, /*#__PURE__*/React.createElement(ChallengeCardData, {
    enableProgressionHighlight: true,
    Button: Button,
    challenge: event.challengeTemplate || undefined,
    Dialog: Dialog,
    event: event,
    geogroup: ownGeogroup,
    hideSponsor: hasHomeTab,
    onFAQClick: () => selectTab('faq'),
    userChallenge: null
  }), props.challenge && props.partner && !familyConfig?.showCompanyParticipantsCount && /*#__PURE__*/React.createElement(_Box, {
    alignItems: "center",
    display: "flex",
    gap: 2,
    marginTop: 2
  }, /*#__PURE__*/React.createElement(ParticipantsIcon, {
    color: "primary",
    fontSize: "small"
  }), /*#__PURE__*/React.createElement(_Typography, {
    variant: "body2"
  }, t('commons.challenges.label_participants_group', {
    title: props.partner.title,
    count: props.challenge.nbMembers
  }))));
  return /*#__PURE__*/React.createElement(_Box, {
    display: "flex",
    flexDirection: "column",
    width: "100%"
  }, !hasHomeTab && challengeCardBlock, /*#__PURE__*/React.createElement(_Tabs, {
    allowScrollButtonsMobile: true,
    onChange: (_, tab) => selectTab(tab),
    onClickCapture: event => {
      if (tabsDragRef.current.moved) {
        event.stopPropagation();
        tabsDragRef.current.moved = false;
      }
    },
    onMouseDown: handleTabsMouseDown,
    ref: tabsRef,
    scrollButtons: "auto",
    sx: {
      cursor: 'grab',
      '&:active': {
        cursor: 'grabbing'
      }
    },
    value: selectedTab,
    variant: "scrollable"
  }, eventsDetailsTab.filter(key => (key !== 'faq' || hasFaqTab) && (key !== 'home' || hasHomeTab) && (key !== 'explorationMap' || hasExplorationMapTab) && (key !== 'explorationMapTest' || hasExplorationMapTestTab) && (key !== 'stats' || hasStatsTab) && (key !== 'potential' || hasPotentialTab) && (key !== 'lots' || hasLotsTab)).map(key => /*#__PURE__*/React.createElement(_Tab, {
    key: key,
    label: t(eventsDetailsTabMap[key].labelKey),
    sx: {
      color: '#2C2E30',
      fontSize: 16,
      fontWeight: 600,
      minHeight: 48,
      textTransform: 'initial'
    },
    value: key
  }))), /*#__PURE__*/React.createElement(_Divider, null), selectedTab === 'home' && (isBttf ? /*#__PURE__*/React.createElement(BttfHome, _extends({
    challengeCardBlock: challengeCardBlock,
    defaultParticipationGroup: defaultParticipationGroup
  }, props, {
    onOwnCompanyClick: handleOwnCompanyClick
  })) : getEventFamily(event.code) === 'mav' ? /*#__PURE__*/React.createElement(MavHome, {
    Button: Button,
    challenge: event.challengeTemplate,
    defaultParticipationGroup: defaultParticipationGroup,
    event: event,
    highlightRegisterCta: props.highlightRegisterCta,
    isMember: isMember,
    onRegisterClick: props.onRegisterClick
  }) : /*#__PURE__*/React.createElement(BfgHome, _extends({
    defaultParticipationGroup: defaultParticipationGroup,
    hasExplorationMapTab: hasExplorationMapTab,
    hasLotsTab: hasLotsTab,
    hasPotentialTab: hasPotentialTab,
    hasStatsTab: hasStatsTab,
    isMember: isMember
  }, props, {
    onFocusOwnCompanyInLeaderboard: handleFocusOwnCompanyInLeaderboard,
    selectLeaderboardScope: setLeaderboardScope
  }))), selectedTab === 'leaderboard' && /*#__PURE__*/React.createElement(React.Fragment, null, hasLeaderboardScoreFutureCard && isMember && props.challenge && /*#__PURE__*/React.createElement(_Box, {
    marginTop: 3
  }, event.period === 'future' ? /*#__PURE__*/React.createElement(LeaderboardScoreFutureCard, {
    Button: Button,
    selectTab: selectTab,
    startDate: event.startDate
  }) : /*#__PURE__*/React.createElement(BfgScoreDetailCard, {
    Button: Button,
    challenge: props.challenge,
    event: event,
    onFocusOwnCompanyInLeaderboard: handleFocusOwnCompanyInLeaderboard,
    ownGeogroup: ownGeogroup,
    selectLeaderboardScope: setLeaderboardScope,
    selectTab: selectTab
  })), /*#__PURE__*/React.createElement(_Box, {
    bgcolor: "#fff",
    border: "1px solid #D9E7FF",
    borderRadius: 4,
    marginY: 3,
    padding: 3
  }, /*#__PURE__*/React.createElement(_Box, {
    alignItems: "center",
    display: "flex",
    flexWrap: "wrap",
    gap: 2,
    justifyContent: "space-between"
  }, /*#__PURE__*/React.createElement(_Typography, {
    fontSize: 20,
    fontWeight: 700
  }, t('commons.challenges.navigation.leaderboard')), /*#__PURE__*/React.createElement(_Box, {
    alignItems: "center",
    display: "flex",
    gap: 2
  }, hasInternalLeaderboardTabs && /*#__PURE__*/React.createElement(_ToggleButtonGroup, {
    exclusive: true,
    onChange: (_, scope) => {
      if (scope) setLeaderboardScope(scope);
    },
    sx: leaderboardToggleSx,
    value: leaderboardScope
  }, /*#__PURE__*/React.createElement(_ToggleButton, {
    value: "national"
  }, "\uD83C\uDF0D ", t('commons.challenges.leaderboard.national_tab')), /*#__PURE__*/React.createElement(_ToggleButton, {
    value: "internal"
  }, "\uD83C\uDFE2 ", t('commons.challenges.leaderboard.internal_tab'))), leaderboardExportSupported && /*#__PURE__*/React.createElement(_Button, {
    disableElevation: true,
    color: "primary",
    disabled: !leaderboardExportHandler || exportingLeaderboard || isNationalCyclistsLeaderboard,
    onClick: handleExportLeaderboardClick,
    startIcon: exportingLeaderboard ? /*#__PURE__*/React.createElement(_CircularProgress, {
      color: "inherit",
      size: 16,
      thickness: 4
    }) : /*#__PURE__*/React.createElement(ExportIcon, null),
    sx: {
      borderRadius: 2,
      fontSize: 14,
      fontWeight: 600,
      paddingX: 2,
      paddingY: 1,
      textTransform: 'initial'
    },
    variant: "contained"
  }, t('commons.actions.export')))), /*#__PURE__*/React.createElement(_Box, {
    display: "flex",
    flexDirection: "column",
    marginTop: 2
  }, leaderboardScope === 'national' ? /*#__PURE__*/React.createElement(React.Fragment, null, (!disableIndividualLeaderboard || holdingGroupId && !isBttf) && /*#__PURE__*/React.createElement(_Box, {
    marginBottom: 2
  }, /*#__PURE__*/React.createElement(_Tabs, {
    onChange: (_, type) => selectLeaderboardType(type),
    value: leaderboardType
  }, holdingGroupId && !isBttf && /*#__PURE__*/React.createElement(_Tab, {
    label: t('commons.challenges.leaderboard.by_holding_group', {
      holdingName: holdingGroupName || ''
    }),
    sx: {
      color: '#2C2E30',
      fontSize: 16,
      fontWeight: 600,
      minHeight: 48,
      textTransform: 'initial'
    },
    value: "holdingCompanies"
  }), /*#__PURE__*/React.createElement(_Tab, {
    label: t('commons.challenges.leaderboard.by_companies'),
    sx: {
      color: '#2C2E30',
      fontSize: 16,
      fontWeight: 600,
      minHeight: 48,
      textTransform: 'initial'
    },
    value: "companies"
  }), !disableIndividualLeaderboard && /*#__PURE__*/React.createElement(_Tab, {
    label: t('commons.challenges.leaderboard.by_cyclists'),
    sx: {
      color: '#2C2E30',
      fontSize: 16,
      fontWeight: 600,
      minHeight: 48,
      textTransform: 'initial'
    },
    value: "individual"
  })), /*#__PURE__*/React.createElement(_Divider, null)), !disableIndividualLeaderboard && leaderboardType === 'individual' ? /*#__PURE__*/React.createElement(IndividualLeaderboardHome, _extends({}, props, {
    isMember: isMember,
    onExportReady: handleLeaderboardExportReady
  })) : isBttf ? /*#__PURE__*/React.createElement(CompaniesLeaderboardHome, _extends({}, props, {
    onOwnCompanyClick: handleOwnCompanyClick
  })) : /*#__PURE__*/React.createElement(EventLeaderboard, {
    hideTabs: true,
    hideTitle: true,
    currentUser: props.currentUser,
    defaultEntityTypes: defaultEntityTypes,
    event: event,
    focusIndex: focusOwnCompanyIndex,
    focusRequestId: focusOwnCompanyRequestId,
    holdingGroupId: holdingGroupId,
    holdingGroupName: holdingGroupName,
    isMember: isMember,
    onExportReady: handleLeaderboardExportReady,
    onlyHoldingGroupCompanies: holdingGroupId ? leaderboardType === 'holdingCompanies' : undefined,
    ownGeogroup: ownGeogroup
  })) : !disableInternalLeaderboard ? /*#__PURE__*/React.createElement(CompanyLeaderboard, _extends({}, props, {
    displayTypes: internalLeaderboardDisplayTypes,
    onExportReady: handleLeaderboardExportReady
  })) : enrolledGeogroupParentId && /*#__PURE__*/React.createElement(SitesEventLeaderboard, {
    disableLeaderboardRestriction: props.disableLeaderboardRestriction,
    event: props.event,
    FakeItemsCard: props.FakeItemsCard,
    freeOfferEmployeesLeaderboardsMaxRowsCount: props.freeOfferEmployeesLeaderboardsMaxRowsCount,
    freeOfferSubgroupsLeaderboardsMaxRowsCount: props.freeOfferSubgroupsLeaderboardsMaxRowsCount,
    isFree: props.isFree,
    onExportReady: handleLeaderboardExportReady,
    parentId: enrolledGeogroupParentId
  })))), selectedTab === 'explorationMap' && hasExplorationMapTab && event.h3GameId && /*#__PURE__*/React.createElement(_Box, {
    paddingBottom: 0
  }, isMember && (event.period === 'current' || event.period === 'past') ? /*#__PURE__*/React.createElement(ExplorationMapGame, {
    gameId: event.h3GameId,
    headquartersGeogroup: ownGeogroup,
    initialColorMode: props.explorationMapColorMode,
    mySiteGeogroup: props.mySiteGeogroup,
    ownGeogroupId: ownGeogroup?.id,
    sites: props.sites
  }) : /*#__PURE__*/React.createElement(_Box, {
    borderRadius: 4,
    overflow: "hidden",
    position: "relative"
  }, /*#__PURE__*/React.createElement(_Box, {
    alt: "Carte d'exploration",
    component: "img",
    src: explorationMapPreview,
    sx: {
      display: 'block',
      filter: !isMember || event.period === 'future' ? 'blur(4px)' : undefined
    },
    width: "100%"
  }), (!isMember || event.period === 'future') && /*#__PURE__*/React.createElement(_Box, {
    alignItems: "center",
    bgcolor: "rgba(0,0,0,0.55)",
    bottom: 0,
    display: "flex",
    flexDirection: "column",
    gap: 2,
    justifyContent: "center",
    left: 0,
    padding: 4,
    position: "absolute",
    right: 0,
    top: 0
  }, /*#__PURE__*/React.createElement(_Box, {
    alt: "",
    component: "img",
    src: explorationMapFlag,
    sx: {
      height: 32,
      width: 32
    }
  }), /*#__PURE__*/React.createElement(_Typography, {
    color: "#fff",
    fontSize: 20,
    fontWeight: 700,
    textAlign: "center"
  }, isMember ? t('commons.challenges.exploration_map.future_title', {
    date: event.startDate.format('D MMMM')
  }) : t('commons.challenges.exploration_map.locked_title')), /*#__PURE__*/React.createElement(_Typography, {
    color: "rgba(255,255,255,0.85)",
    fontSize: 14,
    fontWeight: 400,
    maxWidth: 480,
    textAlign: "center"
  }, isMember ? t('commons.challenges.exploration_map.future_description') : t('commons.challenges.exploration_map.locked_description')), !isMember && props.onRegisterClick && /*#__PURE__*/React.createElement(_Button, {
    disableElevation: true,
    onClick: props.onRegisterClick,
    sx: {
      bgcolor: props.highlightRegisterCta ? '#6236BD' : '#326AC2',
      borderRadius: 2,
      color: '#fff',
      fontWeight: 600,
      paddingX: 3,
      paddingY: 1.5,
      textTransform: 'initial',
      '&:hover': {
        bgcolor: props.highlightRegisterCta ? '#4d2b96' : '#2558a8'
      }
    },
    variant: "contained"
  }, t('commons.challenges.exploration_map.locked_cta'))))), selectedTab === 'explorationMapTest' && event.h3GameId && /*#__PURE__*/React.createElement(_Box, {
    paddingBottom: 0,
    position: "relative"
  }, /*#__PURE__*/React.createElement(_Box, {
    sx: {
      bgcolor: 'rgba(255,244,229,0.95)',
      border: '1px solid #FFB74D',
      borderRadius: 2,
      boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
      color: '#7A4B00',
      fontSize: 13,
      fontWeight: 700,
      left: '50%',
      padding: 1.5,
      position: 'absolute',
      textAlign: 'center',
      top: 16,
      transform: 'translateX(-50%)',
      zIndex: 10
    }
  }, "\u26A0\uFE0F \xC9cran de test \u2014 visible uniquement par les membres Geovelo"), /*#__PURE__*/React.createElement(ExplorationMapGameIntergroup, {
    gameId: event.h3GameId,
    headquartersGeogroup: ownGeogroup,
    mySiteGeogroup: props.mySiteGeogroup,
    ownGeogroupId: ownGeogroup?.id,
    sites: props.sites
  })), selectedTab === 'faq' && (isBfg ? /*#__PURE__*/React.createElement(BfgFAQ, {
    communicationKitUrl: event.communicationKit ?? undefined,
    isAdminOrAnimator: props.employee?.isAdminOrAnimator
  }) : /*#__PURE__*/React.createElement(BttfFAQ, {
    showBttfSite: true
  })), selectedTab === 'stats' && hasStatsTab && (props.StatsContent ? /*#__PURE__*/React.createElement(props.StatsContent, {
    challenge: props.challenge,
    event: event,
    isMember: isMember,
    onNavigateToExplorationMapCommuting: props.onNavigateToExplorationMapCommuting,
    onNavigateToMobilisation: () => selectTab('potential'),
    onRegisterClick: props.onRegisterClick,
    ownGeogroup: ownGeogroup
  }) : /*#__PURE__*/React.createElement(_Box, {
    paddingY: 5
  }, /*#__PURE__*/React.createElement(_Typography, {
    color: "text.secondary",
    textAlign: "center"
  }, t('commons.challenges.stats.placeholder')))), selectedTab === 'potential' && hasPotentialTab && (props.PotentialContent ? /*#__PURE__*/React.createElement(props.PotentialContent, {
    challenge: props.challenge,
    event: event,
    isMember: isMember,
    onRegisterClick: props.onRegisterClick,
    ownGeogroup: ownGeogroup
  }) : /*#__PURE__*/React.createElement(_Box, {
    paddingY: 5
  }, /*#__PURE__*/React.createElement(_Typography, {
    color: "text.secondary",
    textAlign: "center"
  }, t('commons.challenges.potential.placeholder')))), selectedTab === 'lots' && hasLotsTab && (props.LotsContent ? /*#__PURE__*/React.createElement(props.LotsContent, {
    challenge: props.challenge,
    event: event,
    isMember: isMember,
    onRegisterClick: props.onRegisterClick,
    ownGeogroup: ownGeogroup
  }) : /*#__PURE__*/React.createElement(_Box, {
    paddingY: 5
  }, /*#__PURE__*/React.createElement(_Typography, {
    color: "text.secondary",
    textAlign: "center"
  }, t('commons.challenges.lots.placeholder')))));
}
export { eventsDetailsTab, IndividualLeaderboard, CompaniesLeaderboard, CompanyLeaderboard, fakeSites, fakeTeams, fakeRegions, BttfFAQ, BttfPointsRules, CustomPointsDialogButton, EventStatusSubtitle, eventHasHomeTab, ExplorationMapGame };